import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Button } from '@mui/material';
import format from 'string-template';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import FirebaseContext from '../../../../../context/FirebaseContext';
import LeadsContext from '../../../../context/LeadsContext';
import UserContext from '../../../../../context/UserContext';
import Lead from '../../../../Model/Lead';
import LoadingOverlay from '../../../LoadingOverlay';
import { claimLead } from '../../utils';
import texts from './texts.json';
import {
  StyledAlert,
} from './styles';

const ClaimLeadButton = ({
  setSelectedLead,
  setShowQuestionsModal,
  disabled,
}) => {
  const [isClaiming, setIsClaiming] = useState(false);
  const { showToast } = useToast();
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userDoc } = useContext(UserContext);
  const { unclaimedLeadsCollection } = useContext(LeadsContext);
  const availableLeadsCount = unclaimedLeadsCollection?.docs?.length || 0;
  const isComponentMountedRef = useComponentMounted();

  const isButtonDisabled = isClaiming || disabled || availableLeadsCount === 0;

  const handleClaimLead = async () => {
    if (isButtonDisabled) return;

    setIsClaiming(true);
    try {
      const result = await claimLead(remote);

      if (!result.success) {
        throw new Error(result.errMsg);
      }

      const claimedLead = await Lead.getLeadById(result.leadId);
      await Promise.all([
        claimedLead.claimLead(userDoc.id, userDoc.name),
        userDoc.updateIsISOnCall(false),
      ]);

      if (isComponentMountedRef.current) {
        showToast(texts.claimSuccess);
        setSelectedLead(claimedLead);
        setShowQuestionsModal(true);
      }
    } catch (error) {
      showToast(format(texts.claimError, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsClaiming(false);
      }
    }
  };

  return (
    <>
      <StyledAlert severity={availableLeadsCount > 0 ? 'success' : 'info'}>
        {availableLeadsCount > 0
          ? format(texts.availableToClaimBadge, { count: availableLeadsCount })
          : texts.noLeadsAvailable}
          &nbsp;&nbsp;
        <Button
          variant="contained"
          size="small"
          disabled={isButtonDisabled}
          onClick={handleClaimLead}
        >
          {texts.claimNextLead}
        </Button>
      </StyledAlert>
      <LoadingOverlay isLoading={isClaiming} />
    </>
  );
};

ClaimLeadButton.propTypes = {
  setSelectedLead: PropTypes.func.isRequired,
  setShowQuestionsModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ClaimLeadButton.defaultProps = {
  disabled: false,
};

export default observer(ClaimLeadButton);
